import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Badge,
  Button,
  Input,
  Label,
  FormGroup,
  CardHeader
} from 'reactstrap';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// React DnD imports
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Assuming these are available from your project structure
// If these paths are incorrect, please adjust them.
import withRouter from '../../../components/Common/withRouter';
import Spinner from '../../../components/Common/Spinner';

/**
 * ---------------------------------------------------------------------------
 * Plan Customizer – Each plan has its OWN *ordered* feature list.
 * - A feature can belong to multiple plans (pool -> plan is a copy).
 * - Drag inside a plan to reorder (intra-list sorting).
 * - Drag from a plan back to the Pool to remove from that plan.
 * - Drag from one plan to another plan to COPY into the target plan.
 * - Pool shows ALL available features at all times.
 * - Save payload preserves order per plan.
 * ---------------------------------------------------------------------------
 */

// Example: preload saved features per plan
const savedPlans = [
  {
    planId: 'free',
    features: ['basic-scheduling', 'calendar-integration']
  },
  {
    planId: 'professional',
    features: ['basic-scheduling', 'calendar-integration']
  },
  {
    planId: 'enterprise',
    features: ['basic-scheduling', 'calendar-integration']
  },
  {
    planId: 'electric',
    features: []
  }
];

// Mock data for subscription plans
const plans = [
  { id: 'free', name: 'Starter', price: '$0/month', order: 0 },
  { id: 'professional', name: 'Pro', price: '$49/month', order: 1 },
  { id: 'enterprise', name: 'Enterprise', price: 'Custom', order: 2 },
  { id: 'electric', name: 'Electric', price: 'Custom', order: 3 }
];

// Mock data for features
const mockFeatures = [
  {
    id: 'basic-scheduling',
    name: 'Basic Appointment Scheduling',
    category: 'Appointment Management',
    description: 'Schedule and manage basic appointments'
  },
  {
    id: 'calendar-integration',
    name: 'Calendar Integration',
    category: 'Appointment Management',
    description: 'Sync with Google Calendar and Outlook'
  },
  {
    id: 'email-notifications',
    name: 'Email Notifications',
    category: 'Communication',
    description: 'Send email reminders to clients'
  },
  {
    id: 'client-management-basic',
    name: 'Basic Client Management',
    category: 'Client Management',
    description: 'Manage client profiles and history'
  },
  {
    id: 'sms-notifications',
    name: 'SMS Notifications',
    category: 'Communication',
    description: 'Send SMS reminders and updates'
  },
  {
    id: 'recurring-appointments',
    name: 'Recurring Appointments',
    category: 'Appointment Management',
    description: 'Set up recurring appointment schedules'
  },
  {
    id: 'payment-processing',
    name: 'Payment Processing',
    category: 'Payment Processing',
    description: 'Accept online payments from clients'
  },
  {
    id: 'automatic-invoicing',
    name: 'Automatic Invoicing',
    category: 'Payment Processing',
    description: 'Generate and send invoices automatically'
  },
  {
    id: 'unlimited-clients',
    name: 'Unlimited Clients',
    category: 'Client Management',
    description: 'Manage unlimited number of clients'
  },
  {
    id: 'advanced-analytics',
    name: 'Advanced Analytics',
    category: 'Analytics & Reporting',
    description: 'Detailed business insights and metrics'
  },
  {
    id: 'data-export',
    name: 'Data Export',
    category: 'Analytics & Reporting',
    description: 'Export data in various formats'
  },
  {
    id: 'custom-booking-page',
    name: 'Custom Booking Page',
    category: 'Website & Branding',
    description: 'Personalize your online booking page with branding'
  },
  {
    id: 'priority-support',
    name: 'Priority Support',
    category: 'Support',
    description: 'Faster response times for support queries'
  },
  {
    id: 'refund-management',
    name: 'Refund Management',
    category: 'Payment Processing',
    description: 'Advanced refund processing and tracking'
  },
  {
    id: 'client-portal',
    name: 'Client Portal Access',
    category: 'Client Management',
    description: 'Dedicated portal for client self-service and booking'
  },
  {
    id: 'custom-fields',
    name: 'Custom Client Fields',
    category: 'Client Management',
    description: 'Create custom fields for detailed client data'
  },
  {
    id: 'custom-reports',
    name: 'Custom Reports',
    category: 'Analytics & Reporting',
    description: 'Build custom reports and dashboards for specific needs'
  },
  {
    id: 'api-access',
    name: 'API Access',
    category: 'Integration',
    description: 'Full API access for custom integrations with other systems'
  },
  {
    id: 'multi-location',
    name: 'Multi-Location Management',
    category: 'Business Management',
    description: 'Manage multiple business locations from a single account'
  },
  {
    id: 'dedicated-support',
    name: 'Dedicated Account Manager',
    category: 'Support',
    description: 'Personalized support and guidance from a dedicated manager'
  },
  {
    id: 'white-labeling',
    name: 'White Labeling',
    category: 'Website & Branding',
    description: 'Brand the platform with your own logo and colors'
  }
];

// DnD Item Type
const ItemTypes = { FEATURE: 'FEATURE_ITEM' };

/**
 * Utility: get feature by id from master list
 */
const featureById = (id) => mockFeatures.find((f) => f.id === id);

/**
 * Draggable + Droppable Feature Row
 * - Drag source carries { featureId, sourcePlanId, index }
 * - Drop target (hover) supports in-list reordering
 */
const FeatureRow = ({ featureId, index, planId, moveWithinPlan, removeFromPlan }) => {
  const ref = useRef(null);
  const feature = featureById(featureId);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.FEATURE,
      item: { featureId, sourcePlanId: planId, index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() })
    }),
    [featureId, planId, index]
  );

  const [, drop] = useDrop(
    () => ({
      accept: ItemTypes.FEATURE,
      hover: (item, monitor) => {
        // Only handle reordering within SAME plan
        if (!ref.current) return;
        if (item.sourcePlanId !== planId) return;

        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;

        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the item's height
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        moveWithinPlan(planId, dragIndex, hoverIndex);
        item.index = hoverIndex;
      }
    }),
    [index, planId, moveWithinPlan]
  );

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`p-2 mb-2 border rounded d-flex justify-content-between align-items-center ${isDragging ? 'opacity-50' : ''}`}
      style={{ cursor: 'grab' }}
    >
      <div>
        <h6 className="mb-0">{feature?.name}</h6>
        <small className="text-muted">{feature?.description}</small>
      </div>
    </div>
  );
};

/**
 * Column that holds a plan's (ordered) features. Also a drop target.
 * - Drop from Pool or another plan → COPY into this plan (append at end).
 * - Drop from this plan → handled by FeatureRow hover (reorder).
 */
const PlanColumn = ({ plan, featureIds, onDropIntoPlan, moveWithinPlan, removeFromPlan }) => {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.FEATURE,
      drop: (item, monitor) => {
        onDropIntoPlan({
          featureId: item.featureId,
          sourcePlanId: item.sourcePlanId,
          targetPlanId: plan.id
        });
      },
      collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) })
    }),
    [plan?.id, onDropIntoPlan]
  );

  return (
    <div key={plan.id} style={{ minWidth: 300, marginRight: 15 }}>
      <Card className="h-100">
        <CardHeader className="text-center">
          <h5 className="mb-0">{plan.name}</h5>
          <p className="text-muted mb-0">{plan.price}</p>
        </CardHeader>
        <CardBody>
          <h6 className="mb-3">Features:</h6>
          <div
            ref={drop}
            style={{
              minHeight: 150,
              border: isOver ? '2px dashed #0d6efd' : '2px dashed transparent',
              padding: 10,
              borderRadius: 5,
              transition: 'border-color 0.15s ease-in-out'
            }}
          >
            {featureIds.length === 0 && (
              <p className="text-muted">Drag features here to add & sort.</p>
            )}
            {featureIds.map((fid, index) => (
              <FeatureRow
                key={`${plan.id}-${fid}`}
                featureId={fid}
                planId={plan.id}
                index={index}
                moveWithinPlan={moveWithinPlan}
                removeFromPlan={removeFromPlan}
              />
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

/**
 * Feature Pool (drop target: remove from a plan when dropped here)
 * - Always shows the complete catalog to allow copying into any plan.
 */
const FeaturePoolColumn = ({ onDropToPool }) => {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.FEATURE,
      drop: (item) => {
        // Only meaningful when dragging from a plan (remove from that plan)
        onDropToPool(item.sourcePlanId, item.featureId);
      },
      collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) })
    }),
    [onDropToPool]
  );

  return (
    <Col md={3}>
      <Card className="h-100">
        <CardHeader className="text-center bg-dark text-white">
          <h5 className="mb-0">Feature Pool</h5>
          <p className="mb-0">Drag to a plan to add (copies)</p>
        </CardHeader>
        <CardBody>
          <h6 className="mb-3">Available Features:</h6>
          <div
            ref={drop}
            style={{
              minHeight: 150,
              border: isOver ? '2px dashed #17a2b8' : '2px dashed transparent',
              padding: 10,
              borderRadius: 5,
              transition: 'border-color 0.15s ease-in-out'
            }}
          >
            {mockFeatures.map((f) => (
              <PoolFeature key={f.id} feature={f} />
            ))}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

/**
 * Pool feature is draggable (source: "unassigned_pool"). Dropping into a plan copies it there.
 */
const PoolFeature = ({ feature }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.FEATURE,
    item: { featureId: feature.id, sourcePlanId: 'unassigned_pool', index: -1 },
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  }));

  return (
    <div
      ref={drag}
      className={`p-2 mb-2 border rounded d-flex justify-content-between align-items-center ${isDragging ? 'opacity-50' : ''}`}
      style={{ cursor: 'grab' }}
    >
      <div>
        <h6 className="mb-0">{feature.name}</h6>
        <small className="text-muted">{feature.description}</small>
      </div>
    </div>
  );
};

const PlanCustomizer = () => {
  document.title = 'Business | Plan Customizer';

  const [loading, setLoading] = useState(false);

  // planFeatures maps planId -> ordered array of featureIds
  const [planFeatures, setPlanFeatures] = useState(() => {
    const initial = {};
    plans.forEach((p) => {
      initial[p.id] = []; // start empty; adjust if you want defaults
    });
    return initial;
  });

  // Optional fetch to prefill planFeatures from API
  useEffect(() => {
    // Example fetch template (disabled by default):
    // (expects: { [planId]: string[] } where values are ordered feature ids)
    // const fetchAssignments = async () => {
    //   try {
    //     setLoading(true)
    //     const res = await fetch("/api/get-plan-features")
    //     const data = await res.json()
    //     setPlanFeatures((prev) => ({ ...prev, ...data }))
    //   } catch (err) {
    //     console.error(err)
    //     toast.error("Failed to load plan features")
    //   } finally {
    //     setLoading(false)
    //   }
    // }
    // fetchAssignments()
  }, []);

  /**
   * Reorder within the same plan
   */
  const moveWithinPlan = useCallback((planId, fromIndex, toIndex) => {
    setPlanFeatures((prev) => {
      const next = { ...prev };
      const list = [...(next[planId] || [])];
      const [removed] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, removed);
      next[planId] = list;
      return next;
    });
  }, []);

  /**
   * Drop into a plan (copy semantics unless dropping from pool to same plan repeatedly)
   */
  const onDropIntoPlan = useCallback(({ featureId, sourcePlanId, targetPlanId }) => {
    setPlanFeatures((prev) => {
      const next = { ...prev };
      const targetList = [...(next[targetPlanId] || [])];

      // If already in target, do nothing (avoid duplicates)
      if (targetList.includes(featureId)) {
        toast.info(
          `${featureById(featureId)?.name} is already in ${plans.find((p) => p.id === targetPlanId)?.name}.`,
          { autoClose: 1600 }
        );
        return prev;
      }

      targetList.push(featureId);
      next[targetPlanId] = targetList;

      if (sourcePlanId !== 'unassigned_pool' && sourcePlanId === targetPlanId) {
        // No-op: same-plan drop already handled by hover sorting
        return next;
      }

      // If dragging from a plan to the pool is handled elsewhere; plan→plan is COPY
      if (sourcePlanId !== 'unassigned_pool' && sourcePlanId !== targetPlanId) {
        toast.success(
          `${featureById(featureId)?.name} copied to ${plans.find((p) => p.id === targetPlanId)?.name}.`,
          { autoClose: 1400 }
        );
      }

      if (sourcePlanId === 'unassigned_pool') {
        toast.success(
          `${featureById(featureId)?.name} added to ${plans.find((p) => p.id === targetPlanId)?.name}.`,
          { autoClose: 1400 }
        );
      }

      return next;
    });
  }, []);

  /**
   * Remove a feature from a specific plan (used by button and pool drop)
   */
  const removeFromPlan = useCallback((planId, featureId) => {
    setPlanFeatures((prev) => {
      const next = { ...prev };
      next[planId] = (next[planId] || []).filter((fid) => fid !== featureId);
      return next;
    });
    toast.success(
      `${featureById(featureId)?.name} removed from ${plans.find((p) => p.id === planId)?.name}.`,
      { autoClose: 1400 }
    );
  }, []);

  /**
   * Handle drop to Pool: remove from the source plan (if any).
   */
  const onDropToPool = useCallback(
    (sourcePlanId, featureId) => {
      if (!sourcePlanId || sourcePlanId === 'unassigned_pool') return;
      removeFromPlan(sourcePlanId, featureId);
    },
    [removeFromPlan]
  );

  const handleSaveAssignments = async () => {
    // Build payload preserving order for each plan
    const payload = plans.map((p) => ({ planId: p.id, features: planFeatures[p.id] || [] }));
    try {
      // setLoading(true)
      const response = await fetch('/api/save-plan-features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success('Assignments saved!', { autoClose: 1800 });
    } catch (error) {
      console.error(error);
      toast.error('Error saving feature assignments.', { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div>
        <Container fluid>
          {loading ? (
            <Spinner setLoading={setLoading} />
          ) : (
            <DndProvider backend={HTML5Backend}>
              <Row className="mt-4">
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <h4 className="card-title mb-2">Customize Feature Tiers</h4>
                      <p className="text-muted mb-4">
                        Drag features from the <strong>Feature Pool</strong> into a plan to{' '}
                        <strong>copy</strong> them there. Drag within a plan to{' '}
                        <strong>reorder</strong>. Drag back to the pool (or use Remove) to{' '}
                        <strong>remove</strong> from that plan.
                      </p>

                      <div className="d-flex flex-row overflow-auto pb-2">
                        <FeaturePoolColumn onDropToPool={onDropToPool} />
                        {plans.map((plan) => (
                          <PlanColumn
                            key={plan.id}
                            plan={plan}
                            featureIds={planFeatures[plan.id] || []}
                            onDropIntoPlan={onDropIntoPlan}
                            moveWithinPlan={moveWithinPlan}
                            removeFromPlan={removeFromPlan}
                          />
                        ))}
                      </div>

                      <div className="text-end mt-3">
                        <Button color="primary" onClick={handleSaveAssignments}>
                          Save Changes
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </DndProvider>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(PlanCustomizer);
